import faker from 'faker'
import modules from 'modules'
import colors from '@bit/atd.web.colors'
import { format } from 'date-fns'
import { STATIC_ROUTES } from 'constants/routeNamesStatic'
import { contentLabels } from 'modules/benefitsCenter/components/FeaturedBenefit/contentLabels'
import { TaxonomySearchItem } from 'lib/graphql/graphqlTypes'
import qs from 'qs'

export enum ActivityAction {
  COMMENT = 'COMMENT',
  CREATED = 'CREATED',
  SHARED = 'SHARED',
  FOLLOW = 'FOLLOW',
  GROUP = 'GROUP',
}
type Elem = ElemArray
type ElemArray = (Elem | string | number)[]
type ContentTypesList = {
  lvl0: string
  lvl1: string
  lvl2: string
}

/**
 *
 */
class Helper {
  private static dateCleaner(value?: number | string | Date): number {
    if (!value) {
      return 0
    }

    if (value instanceof Date) {
      return value.getTime() / 1000
    }

    if (typeof value === 'number') {
      const trunc = Math.trunc(value)
      const numDigits = Math.max(Math.floor(Math.log10(Math.abs(value))), 0) + 1
      if (numDigits >= 13) {
        return value / 1000
      }
      return trunc
    }

    return Date.parse(value) / 1000
  }

  /**
   *
   * @param url
   */
  public static openInNewTab(url: string): void {
    const newWindow = window.open(url, '_blank')
    if (newWindow) newWindow.opener = null
  }

  /**
   *
   * @param date
   */
  public static globalDateFormatter(date: number): string {
    const newDate = Helper.dateCleaner(date)
    return format(newDate * 1000, 'MMMM dd yyyy')
  }

  /**
   *
   * @param dateFrom
   * @param dateTo
   */
  public static rangeDateFormatter(dateFrom?: number, dateTo?: number): string {
    const newDateFrom = Helper.dateCleaner(dateFrom)
    const newDateTo = Helper.dateCleaner(dateTo)
    return `${format(newDateFrom * 1000, 'MMM dd')} - ${format(
      newDateTo * 1000,
      'MMM dd, yyyy',
    )}`
  }

  /**
   *
   * @param time
   * @param timeStampMultiplier
   */
  public static timestampFormatter(
    time: number | string,
    timeStampMultiplier = 1,
  ): string {
    let timeSeconds

    if (typeof time === 'string') {
      const split = time.split(':')
      const splitReverse = split.reverse()

      timeSeconds = splitReverse.reduce((acc, curr, index) => {
        const multiplier = Math.pow(60, index)
        return acc + parseInt(curr) * multiplier
      }, 0)
    } else {
      timeSeconds = Number((time * timeStampMultiplier) / 1000)
    }

    const h = Math.floor(timeSeconds / 3600)
    const m = Math.floor((timeSeconds % 3600) / 60)
    const s = Math.floor((timeSeconds % 3600) % 60)

    const hDisplay = h > 0 ? h.toString() + ':' : '00:'
    const mDisplay = m > 0 ? m.toString() + ':' : '00:'
    const sDisplay = s > 0 ? s : '00'

    return `${hDisplay}${mDisplay}${sDisplay}`
  }

  /**
   *
   * @param n
   * @param dp
   */
  public static formatThousandsWithRounding(n: number, dp: number) {
    const w = n.toFixed(dp) as string
    const k = parseInt(w)
    const b = n < 0 ? 1 : 0
    const u = Math.abs(parseFloat(w) - k)
    const d = ('' + u.toFixed(dp)).substr(2, dp)
    const s = `${k}`

    let i = s.length,
      r = ''

    while ((i -= 3) > b) {
      r = ',' + s.substr(i, 3) + r
    }

    return s.substr(0, i + 3) + r + (d ? '.' + d : '')
  }

  /**
   *
   * @param n
   * @param dp
   */
  public static formatThousandsNoRounding = function (n: number, dp: number) {
    const e = '',
      s = e + n,
      l = s.length,
      b = n < 0 ? 1 : 0,
      i = s.lastIndexOf('.')

    let j = i === -1 ? l : i,
      r = e

    const d = s.substr(j + 1, dp)

    while ((j -= 3) > b) {
      r = ',' + s.substr(j, 3) + r
    }

    return (
      s.substr(0, j + 3) +
      r +
      (dp
        ? '.' + d + (d.length < dp ? '00000'.substr(0, dp - d.length) : e)
        : e)
    )
  }

  /**
   *
   * @param date
   */
  public static smallDateFormatter(date: number): string {
    const newDate = Helper.dateCleaner(date)
    return format(newDate * 1000, 'MMMM dd, yyyy')
  }

  /**
   *
   * @param date
   */
  public static miniDateFormatter(date: number): string {
    const newDate = Helper.dateCleaner(date)
    return format(newDate * 1000, 'MMMM dd')
  }
  /**
   *
   * @param date
   */
  public static condensedDateFormatter(date: number): string {
    const newDate = Helper.dateCleaner(date)
    return format(newDate * 1000, 'MM/dd/yyyy')
  }

  public static getMonthAndYear(date: number): string {
    return format(new Date(date * 1000), 'MMMM yyyy')
  }

  /**
   *
   * @param date
   */
  public static hourDateFormatter(date: number): string {
    const newDate = Helper.dateCleaner(date)
    return format(newDate * 1000, 'h:mm a')
  }

  /**
   *
   * @param date
   */
  public static getYearFromIso(date: number): string {
    return format(date * 1000, 'yyyy')
  }

  public static bytesToMegaBytes(bytes: number): string {
    return (bytes / (1024 * 1024)).toFixed(2)
  }

  /**
   *
   * @param enumVar
   */
  public static enumToArray(enumVar: string[]): string[] {
    return Object.values(enumVar)
  }

  /**
   *
   * @param enumVar
   */
  public static getRandomEnum(enumVar: any): any {
    const arr = Helper.enumToArray(enumVar)
    return arr[faker.random.number({ min: 0, max: arr.length - 1 })]
  }

  /**
   *
   * @param typesArray
   */
  public static getRandomArrayVal(typesArray: ElemArray): any {
    return typesArray[Math.floor(Math.random() * typesArray.length)]
  }

  /**
   *
   * @param key
   */
  public static getPathFromRouteKey(key: string): string {
    const is_header_mode = parseInt(process.env.REACT_APP_HEADER_MODE as string)
    const res = is_header_mode
      ? STATIC_ROUTES.find((route: any) => route.key === key)
      : modules.routes.find((route) => route.key === key)

    return res?.hasOwnProperty('key') ? res.path : '/'
  }

  /**
   *
   * @param keyword
   * @param refinementList
   */
  public static constructSearchURL({
    keyword,
    refinementList,
  }: {
    keyword?: string
    refinementList?: { refinement: string; value: string }[]
  }): string {
    const keywordSearch = keyword ? `query=${encodeURI(keyword)}&` : ''
    const query =
      refinementList
        ?.map((item) => {
          return encodeURI(
            `refinementList[${item.refinement}][0]=${item.value}`,
          )
        })
        .join('&') || ''

    return `/search?${keywordSearch}${query}`
  }

  /**
   *
   * @param str
   */
  public static getAcronymFromSentence(str?: string): string {
    if (!str) {
      return ''
    }
    return str
      .split(/\s/)
      .reduce((response, word) => (response += word.slice(0, 1)), '')
      .toUpperCase()
  }

  /**
   * @todo this should go in the theme layer
   * @param isBrand
   */
  public static randomColorChooser(isBrand = false): string {
    if (isBrand) {
      return colors['profileAvatarYellow']
    }

    const shortlist: (keyof typeof colors)[] = [
      'profileAvatarYellow',
      'profileAvatarAmberYellow',
      'profileAvatarLime',
      'profileAvatarGreen',
      'profileAvatarBlue',
      'profileAvatarIndigio',
      'profileAvatarViolet',
      'profileAvatarDeepPurple',
      'profileAvatarPurple',
      'profileAvatarRose',
    ]
    const key = Math.ceil(Math.random() * shortlist.length)
    return colors[shortlist[key]]
  }

  /**
   *
   * @param word
   */
  public static articleChooser(word: string | null | undefined): string {
    if (!word) return ''

    const vowels = 'aeiouAEIOU'

    const first = word[0]

    if (vowels.indexOf(first) >= 0) return 'an'
    else return 'a'
  }

  /**
   *
   * @param date
   */
  public static getStatus = (date: any): string => {
    const newDate = Helper.dateCleaner(date)
    return new Date(newDate * 1000) > new Date() ? 'Upcoming' : 'Attended'
  }

  /**
   *
   * @param data
   * @param key
   */
  public static constantsLabelFinder(
    data: { key: string; value: string }[],
    key: string,
  ): string {
    if (!key) return ''

    if (data.length) {
      const item = data.find((item) => item.key === key)
      if (item?.value) {
        return item.value
      }
    }

    return key
  }

  /**
   *
   * @param key
   */
  public static actionTextHelper = (key: ActivityAction): string => {
    const actionsStrings = {
      [ActivityAction.CREATED]: 'Posted ',
      [ActivityAction.SHARED]: 'Shared ',
      [ActivityAction.COMMENT]: 'Commented on your ',
      [ActivityAction.FOLLOW]: 'Is now following ',
      [ActivityAction.GROUP]: 'I group here',
    }

    if (actionsStrings.hasOwnProperty(key)) {
      return actionsStrings[key]
    }
    return 'Has updated'
  }

  /**
   *
   * @param key
   * @param value
   */
  public static labelLookup(key: string, value: string): string {
    if (!key) return ''

    const labels = {
      memberType: {
        MEMBER: 'Standard Member',
        PROFESSIONAL_PLUS: 'Professional Plus',
      },
    }
    if (!labels.hasOwnProperty(key)) {
      return key
    }

    return this.resolve(`${key}.${value}`, labels)
  }

  private static resolve(path: any, obj: any): string {
    return path.split('.').reduce(function (prev: any, curr: any) {
      return prev ? prev[curr] : ''
    }, obj)
  }

  /**
   *
   * @param schemas
   */
  public static merge(...schemas: any): any {
    const [first, ...rest] = schemas

    return rest.reduce(
      (mergedSchemas: any, schema: any) => mergedSchemas.concat(schema),
      first,
    )
  }

  /**
   *
   * @param path
   * @param history
   */
  public static pushPath(path: string, history: any): void {
    path.startsWith('/') ? history.push(path) : (window.location.href = path)
  }

  /**
   *
   * @param contentTypes
   */
  public static contentTypeFormatter = (
    contentTypes?: TaxonomySearchItem | null,
  ): string => {
    if (!contentTypes) {
      return ''
    }
    const items: (keyof ContentTypesList)[] = ['lvl2', 'lvl1', 'lvl0']

    const contentType = items.reduce(
      (acc: string, item: keyof ContentTypesList) => {
        if (acc) return acc

        const types = contentTypes[item]?.split('>')
        return types ? types[types.length - 1].replace(' ', '') : ''
      },
      '',
    )

    const contentLabel = contentLabels.find(({ key }) => key === contentType)
    return contentLabel?.label || contentType
  }

  /**
   *
   * @param index
   * @param filter
   */
  public static createSearchLink(index: string, filter: string): string {
    const createURL = (state: Record<string, unknown>) =>
      qs.stringify(state, { addQueryPrefix: true, format: 'RFC1738' })

    const searchState = {
      hierarchicalMenu: {
        'contentTypes.lvl0': filter,
      },
    }
    return `${process.env.PUBLIC_URL}/search/${index}${createURL(
      searchState,
    )}&toggle%5BmemberBenefit%5D=true&page=1`
  }

  /**
   *
   * @param state
   */
  public static createURL = (state: Record<string, unknown>) =>
    qs.stringify(state, { addQueryPrefix: true, format: 'RFC1738' })
}

export default Helper
